import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, IconButton, Tooltip, Typography, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import AddToPlaylistDialog from '../AddToPlaylistDialog';
import { Play, Eye, Plus, Music2, ListMusic, Check } from 'lucide-react';
import ThumbnailZoom from '../common/ThumbnailZoom';
import { useTranslation } from 'react-i18next';
import { formatSecondsToTime } from '../../utils/playlistUtils';

export default function SearchResult({ 
  videos, 
  setPreviewVideoId, 
  openVideoPlayer, 
  setPlaylist, 
  playlist, 
  setReloadPlaylist,
  currentPlaylist,
  myPlaylists = [],
  userPresetPlaylists = [],
  onAddToPlaylist,
  setCurrentPlaylistItem,
  videoId,
  isIntegratedScreen = false,
  showPreview = true,
}) {
  const { t } = useTranslation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [playlistMenuAnchor, setPlaylistMenuAnchor] = useState(null);
  const [selectedVideoForMenu, setSelectedVideoForMenu] = useState(null);
  const [playbackProgress, setPlaybackProgress] = useState({});

  // Estados para el diálogo "¿A quién asignar la canción?"
  const [openAddToPlaylistModal, setOpenAddToPlaylistModal] = useState(false);
  const [videoToAdd, setVideoToAdd] = useState(null);
  const [targetPlaylistForAdd, setTargetPlaylistForAdd] = useState(null);

  // Recopilar todos los participantes asignados en todas las listas disponibles
  const existingParticipants = React.useMemo(() => {
    const participantsSet = new Set();

    const scanItems = (items) => {
      if (!Array.isArray(items)) return;
      items.forEach((item) => {
        if (item && item.assignedTo && typeof item.assignedTo === 'string') {
          const trimmed = item.assignedTo.trim();
          if (trimmed) {
            participantsSet.add(trimmed);
          }
        }
      });
    };

    // 1. Escanear todas las listas propias
    if (Array.isArray(myPlaylists)) {
      myPlaylists.forEach((pl) => scanItems(pl?.items));
    }

    // 2. Escanear la lista activa en pantalla
    scanItems(playlist);

    // 3. Escanear listas preset si tienen ítems asignados
    if (Array.isArray(userPresetPlaylists)) {
      userPresetPlaylists.forEach((pl) => scanItems(pl?.items));
    }

    return Array.from(participantsSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [myPlaylists, playlist, userPresetPlaylists]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Escuchar eventos de progreso para actualizar el indicador circular
  useEffect(() => {
    const handleProgress = (e) => {
      const { videoId: progVidId, progress } = e.detail || {};
      if (progVidId && progress !== undefined) {
        setPlaybackProgress(prev => ({
          ...prev,
          [progVidId]: progress,
        }));
      }
    };

    window.addEventListener('garaoke_playback_progress', handleProgress);

    let bc = null;
    try {
      if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
        bc = new BroadcastChannel('garaoke_player_sync');
        bc.onmessage = (event) => {
          if (event.data?.type === 'playbackProgress') {
            const { videoId: progVidId, progress } = event.data;
            if (progVidId && progress !== undefined) {
              setPlaybackProgress(prev => ({
                ...prev,
                [progVidId]: progress,
              }));
            }
          }
        };
      }
    } catch (e) {}

    return () => {
      window.removeEventListener('garaoke_playback_progress', handleProgress);
      if (bc) bc.close();
    };
  }, []);

  const getTrackDuration = (videoObj) => {
    if (videoObj?.duration) return videoObj.duration;
    const vidId = getVideoId(videoObj);
    if (vidId) {
      try {
        const cached = JSON.parse(localStorage.getItem('garaoke_duration_cache') || '{}');
        if (cached[vidId]) return cached[vidId];
      } catch (e) {}
    }
    return null;
  };

  const videoPlayNow = (videoId) => {
    openVideoPlayer(videoId);
  };

  const getVideoId = (video) => {
    if (!video) return null;
    if (typeof video.id === 'string') return video.id;
    if (video.id?.videoId) return video.id.videoId;
    if (video.videoId) return video.videoId;
    return null;
  };

  const playNow = (video) => {
    const vidId = getVideoId(video);
    if (!vidId) return;
    setCurrentPlaylistItem({ playlist: null, item: vidId });
    videoPlayNow(vidId);
  };

  const handleAddClick = (event, video) => {
    // Si hay más de una lista propia, abrir menú para elegir a cuál agregar
    if (myPlaylists && myPlaylists.length > 1) {
      setPlaylistMenuAnchor(event.currentTarget);
      setSelectedVideoForMenu(video);
    } else {
      // 0 o 1 lista de usuario: abrir diálogo de asignación directamente
      const singleList = myPlaylists.length === 1 ? myPlaylists[0] : null;
      setVideoToAdd(video);
      setTargetPlaylistForAdd(singleList);
      setOpenAddToPlaylistModal(true);
    }
  };

  const handleSelectPlaylistForAdd = (targetList) => {
    handleClosePlaylistMenu();
    setVideoToAdd(selectedVideoForMenu);
    setTargetPlaylistForAdd(targetList);
    setOpenAddToPlaylistModal(true);
  };

  const handleClosePlaylistMenu = () => {
    setPlaylistMenuAnchor(null);
  };

  const handleCloseAddToPlaylistModal = () => {
    setOpenAddToPlaylistModal(false);
    setVideoToAdd(null);
    setTargetPlaylistForAdd(null);
    setSelectedVideoForMenu(null);
  };

  const handleConfirmAddToPlaylist = (videoObj, assignedTo = '') => {
    if (onAddToPlaylist && videoObj) {
      const targetId = targetPlaylistForAdd ? targetPlaylistForAdd.id : null;
      onAddToPlaylist(videoObj, targetId, assignedTo);
    }
    handleCloseAddToPlaylistModal();
  };

  return (
    <>
      <AddToPlaylistDialog
        show={openAddToPlaylistModal}
        video={videoToAdd}
        close={handleCloseAddToPlaylistModal}
        addToPlayList={handleConfirmAddToPlaylist}
        targetPlaylistName={targetPlaylistForAdd?.name || ''}
        existingParticipants={existingParticipants}
        isIntegratedScreen={isIntegratedScreen}
      />
      <Card elevation={3} style={{ padding: '10px 12px', margin: 0, height: '100%', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }} >
        <CardContent style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
          <Box sx={{ mb: 0.8, flexShrink: 0 }}>
            <Typography variant="h6" component="div">
              {t('search.results')}
            </Typography>
          </Box>
          <div className="search-result-scroll scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {videos?.map((video, index) => {
              const currentVidId = getVideoId(video);
              const isPlayingThis = Boolean(videoId && currentVidId && videoId === currentVidId);
              const trackDur = getTrackDuration(video);

              return (
                <Box 
                  key={index} 
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    p: 1,
                    mb: 0.8,
                    borderRadius: '10px',
                    background: isPlayingThis 
                      ? 'rgba(0, 255, 136, 0.08)' 
                      : 'rgba(255, 255, 255, 0.03)',
                    border: isPlayingThis 
                      ? '1px solid rgba(0, 255, 136, 0.4)' 
                      : '1px solid rgba(255, 255, 255, 0.05)',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      background: isPlayingThis ? 'rgba(0, 255, 136, 0.12)' : 'rgba(255, 255, 255, 0.06)',
                      borderColor: isPlayingThis ? '#00ff88' : 'rgba(255, 255, 255, 0.12)'
                    }
                  }}
                >
                  <ThumbnailZoom 
                    src={video.snippet.thumbnails.high?.url || video.snippet.thumbnails.default?.url} 
                    highResSrc={video.snippet.thumbnails.high?.url || video.snippet.thumbnails.medium?.url}
                    alt={video.snippet.title}
                    title={video.snippet.title}
                    placement="right"
                    onClick={showPreview && typeof setPreviewVideoId === 'function' ? () => setPreviewVideoId(currentVidId) : undefined}
                  />

                  <Box 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      borderRadius: '8px', 
                      background: isPlayingThis 
                        ? 'linear-gradient(135deg, rgba(0, 255, 136, 0.3) 0%, rgba(0, 212, 255, 0.2) 100%)'
                        : 'linear-gradient(135deg, rgba(255, 45, 155, 0.2) 0%, rgba(180, 77, 255, 0.2) 100%)',
                      border: isPlayingThis ? '1px solid #00ff88' : '1px solid rgba(255, 45, 155, 0.3)',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Music2 size={16} color={isPlayingThis ? '#00ff88' : '#ff2d9b'} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Typography 
                      title={video.snippet.title}
                      sx={{ 
                        fontSize: '0.84rem', 
                        fontWeight: 600, 
                        lineHeight: 1.25, 
                        color: isPlayingThis ? '#00ff88' : '#fff',
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis' 
                      }}
                    >
                      {video.snippet.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.2 }}>
                      <Typography 
                        sx={{ 
                          fontSize: '0.67rem', 
                          color: isPlayingThis ? 'rgba(0, 255, 136, 0.75)' : 'rgba(255, 255, 255, 0.4)', 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}
                      >
                        {video.snippet.channelTitle}
                      </Typography>
                      {trackDur !== null && (
                        <>
                          <Typography 
                            component="span" 
                            sx={{ 
                              fontSize: '0.62rem', 
                              color: 'rgba(255, 255, 255, 0.22)', 
                              lineHeight: 1, 
                              userSelect: 'none' 
                            }}
                          >
                            •
                          </Typography>
                          <Typography 
                            component="span"
                            sx={{ 
                              fontSize: '0.67rem', 
                              color: 'rgba(255, 255, 255, 0.45)', 
                              fontWeight: 600,
                              lineHeight: 1,
                              background: 'rgba(255, 255, 255, 0.05)',
                              px: 0.5,
                              py: 0.1,
                              borderRadius: '3px'
                            }}
                          >
                            {formatSecondsToTime(trackDur)}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, flexShrink: 0 }}>
                    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      {isPlayingThis && (
                        <svg
                          style={{
                            position: 'absolute',
                            top: -3,
                            left: -3,
                            width: 30,
                            height: 30,
                            pointerEvents: 'none',
                            transform: 'rotate(-90deg)',
                            zIndex: 1,
                          }}
                          viewBox="0 0 32 32"
                        >
                          <circle
                            cx="16"
                            cy="16"
                            r="13.5"
                            fill="none"
                            stroke="rgba(0, 255, 136, 0.18)"
                            strokeWidth="2.2"
                          />
                          <circle
                            cx="16"
                            cy="16"
                            r="13.5"
                            fill="none"
                            stroke="#00ff88"
                            strokeWidth="2.2"
                            strokeDasharray={84.82}
                            strokeDashoffset={84.82 * (1 - (playbackProgress[currentVidId] || 0))}
                            strokeLinecap="round"
                            style={{
                              transition: 'stroke-dashoffset 0.35s linear',
                              filter: 'drop-shadow(0 0 4px rgba(0, 255, 136, 0.7))',
                            }}
                          />
                        </svg>
                      )}
                      <Tooltip title={isPlayingThis ? t('search.playingNow') : t('search.playNow')}>
                        <IconButton 
                          size="small" 
                          onClick={() => playNow(video)}
                          sx={{ 
                            p: '4px', 
                            borderRadius: '6px',
                            color: isPlayingThis ? '#00ff88' : 'rgba(255, 255, 255, 0.7)',
                            background: isPlayingThis ? 'rgba(0, 255, 136, 0.12)' : 'transparent',
                            boxShadow: isPlayingThis ? '0 0 8px rgba(0, 255, 136, 0.25)' : 'none',
                            '&:hover': { color: '#00ff88', background: 'rgba(0, 255, 136, 0.15)' } 
                          }}
                        >
                          <Play size={15} fill={isPlayingThis ? '#00ff88' : 'currentColor'} />
                        </IconButton> 
                      </Tooltip>
                    </Box>
                    {showPreview && (
                      <Tooltip title={t('search.preview')}>
                        <IconButton 
                          size="small" 
                          onClick={() => setPreviewVideoId && setPreviewVideoId(currentVidId)}
                          sx={{ 
                            p: '4px', 
                            borderRadius: '6px',
                            color: '#00d4ff', 
                            '&:hover': { background: 'rgba(0, 212, 255, 0.15)' } 
                          }}
                        >
                          <Eye size={15} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={myPlaylists && myPlaylists.length > 1 ? t('search.addToPlaylistMenu') : t('search.addToPlaylist')}>
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleAddClick(e, video)}
                        sx={{ 
                          p: '4px', 
                          borderRadius: '6px',
                          color: '#ff2d9b', 
                          '&:hover': { background: 'rgba(255, 45, 155, 0.15)' } 
                        }}
                      >
                        <Plus size={16} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Menu
        anchorEl={playlistMenuAnchor}
        open={Boolean(playlistMenuAnchor)}
        onClose={handleClosePlaylistMenu}
        slotProps={{
          paper: {
            sx: {
              background: 'rgba(18, 8, 32, 0.96)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(255, 45, 155, 0.3)',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6), 0 0 15px rgba(255, 45, 155, 0.2)',
              minWidth: 220,
              py: 0.5,
            }
          }
        }}
      >
        <Box sx={{ px: 1.5, py: 0.8, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#ff2d9b', textTransform: 'uppercase', letterSpacing: 0.8 }}>
            {t('search.selectPlaylistToAdd')}
          </Typography>
        </Box>
        {myPlaylists.map((pl) => {
          const isCurrentActive = pl.id === currentPlaylist || pl.name === currentPlaylist;
          const itemCount = (pl.items || []).length;

          return (
            <MenuItem
              key={pl.id}
              onClick={() => handleSelectPlaylistForAdd(pl)}
              sx={{
                py: 1,
                px: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: '0.85rem',
                color: '#ffffff',
                transition: 'all 0.15s ease',
                '&:hover': {
                  background: 'rgba(255, 45, 155, 0.12)',
                  color: '#ff2d9b'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 'auto', color: isCurrentActive ? '#00ff88' : '#b44dff' }}>
                <ListMusic size={16} />
              </ListItemIcon>
              <ListItemText
                primary={pl.name}
                secondary={`${itemCount} ${itemCount === 1 ? t('playlist.song') : t('playlist.songs')}`}
                primaryTypographyProps={{
                  fontSize: '0.84rem',
                  fontWeight: isCurrentActive ? 600 : 500,
                  color: isCurrentActive ? '#00ff88' : '#ffffff',
                  noWrap: true
                }}
                secondaryTypographyProps={{
                  fontSize: '0.7rem',
                  color: 'rgba(255, 255, 255, 0.45)'
                }}
              />
              {isCurrentActive && (
                <Check size={14} color="#00ff88" style={{ marginLeft: 'auto', flexShrink: 0 }} />
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}